// EXEMPT
import getOSType from "@/util/get-os-type.mjs";
import globals from "@/util/global-whitelist.mjs";
import hasQueryString from "@/util/has-query-string.mjs";
import symbolName from "@/util/symbol-name.mjs";

const OS_TYPE = getOSType();

// Internal delimiter when converting array paths to string.
export const PATH_DELIMITER = "$";

// An object containing the `isPersistent` symbol will be persisted.
// The value corresponding to the symbol can be a timestamp, to set a custom
// expiry time.
// `isVolatile` will do the opposite.
export const isPersistent: unique symbol = symbolName("is-persistent");
export const isVolatile: unique symbol = symbolName("is-volatile");

// This symbol is mainly for internal use: it prevents unnecessary double writes.
export const isPopulatedFromDB = symbolName("is-populated-from-db");

// So what is this symbol for? This is mainly used if we expect a part of the
// initial state to be hydrated from local DB only. If it's the initial object,
// using `readData` on it will trigger a DB read.
export const isInitial: unique symbol = symbolName("initial");

// Time To Live (TTL) 💀🪦
const HUMAN_LIFE_MAX_YEARS = 100;
export const MAX_TIME =
  Date.now() + 1000 * 60 * 60 * 24 * 365 * HUMAN_LIFE_MAX_YEARS;

// Expose dev with query param...
export const IS_DEV_QUERY = hasQueryString("__BLITZ_DEV__");

export const IS_NO_DEV_QUERY = hasQueryString("__BLITZ_NO_DEV__");

export const IS_STAGING = globals.localStorage?.getItem("env") === "staging";

export const IS_DEV_SERVER = globals.location?.host === "localhost:3000";
export const IS_DEV_NODE = globals.location?.host === "localhost:8080";

export const IS_CF_PAGES = /^.*?\.pages\.dev$/.test(globals.location?.host);

export const IS_DEV =
  !IS_NO_DEV_QUERY &&
  (IS_DEV_SERVER || IS_DEV_NODE || IS_CF_PAGES || IS_DEV_QUERY);

export const IS_TESTING = Boolean(
  globals.__BLITZ_TESTING__ || globals.__BLITZ_TEST_MOCKS__,
);

export const GIT_COMMIT_HASH =
  typeof __GIT_COMMIT_HASH__ !== "undefined" && __GIT_COMMIT_HASH__;

// Check if it's Node.js regardless of headless.
export const IS_NODE = typeof process !== "undefined";

// Check if we're running in Node.js (not headless).
export const IS_NODE_BROWSERLESS =
  IS_NODE &&
  (typeof navigator === "undefined" ||
    globals.navigator?.userAgent?.startsWith("Node.js"));

// Injected from blitz-core.
export const IS_APP = typeof globals.__BLITZ__ !== "undefined";

// Special note for detection here: we can trivially detect if it's the
// production website by the domain, blitz.cn. What's more interesting is
// enabling this flag by default for CN developers + CN web server.
//
// For example, __BLITZ_CN__ can be provided as a CLI argument to the build
// script for the server. This wouldn't be runtime, but it may be necessary
// only for the web server. Same might apply for dev environment.
export const IS_CHINA = Boolean(
  globals.location?.hostname === "blitz.cn" ||
    // This is a meta tag set by SSR.
    globals.document?.querySelector(`meta[itemprop="china"]`) ||
    globals.__BLITZ_CN__,
);
export const IS_MAC = OS_TYPE === "darwin";

export const IS_APRIL_FOOLS = (() => {
  const now = new Date();
  return now.getMonth() === 3 && now.getDate() === 1;
})();

export const IS_HALLOWEEN = (() => {
  const now = new Date();
  return now.getMonth() === 9 && now.getDate() === 31;
})();

export const IS_PROBUILDS =
  hasQueryString("__BLITZ_PROBUILDS_NET__") ||
  (!IS_CF_PAGES && /probuilds.net/.test(globals.location?.hostname));

export const IS_AGENT_SELECT =
  hasQueryString("__BLITZ_AGENT_SELECT_NET__") ||
  (!IS_CF_PAGES && /agentselect.net/.test(globals.location?.hostname));

export const IS_CHAMPION_SELECT =
  hasQueryString("__BLITZ_CHAMPION_SELECT_NET__") ||
  (!IS_CF_PAGES && /championselect.net/.test(globals.location?.hostname));

export const IS_LOLSTATS =
  hasQueryString("__BLITZ_LOLSTATS_COM__") ||
  (!IS_CF_PAGES && /lolstats.com/.test(globals.location?.hostname));

export const IS_TFTCOMPS =
  hasQueryString("__BLITZ_TFTCOMPS_GG__") ||
  (!IS_CF_PAGES && /tftcomps.gg/.test(globals.location?.hostname));

declare global {
  interface Object {
    [isPersistent]?: number | boolean;
    [isVolatile]?: boolean;
    [isInitial]?: boolean;
  }
}
